import React, { useState, useEffect, useMemo, FC } from 'react';
import { ComponentRendering, isEditorActive, RichText, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { useTranslation } from 'react-i18next';
import { iconSize } from '../../../../constants/icon.constants';
import { ProductCompatibleLoaderListingModel } from '../../models/productCompatibleLoaderListing.model';
import { TCHCompatibleLoadersProps } from '../../types/productCompatibleLoaderListing.types';
import useExperiencerEditorPlaceholder from '../../../../hooks/useExperiencerEditorPlaceholder';
import { googleClickCardEvent, googleClickRichTextEvent, googleSeeMoreCardEvent } from '../../../../utils/analytics';
import { isEmptyResponse } from '../../../../utils/checkEmptyResponse';
import { appendToUrlPath } from '../../../../utils';
import { windowService, documentService } from '../../../../utils/window.utils';
import { appendRootToRichTextInternalLinks } from '../../../../utils/appendRootToRichTextInternalLinks.utils';
import CompatibleLoadersTabs from '../compatibleLoadersTabs';
import AnalyticsVisibleEvent from '../../../@shared/analyticsVisibleEvent';
import RoutableNotEditableLink from '../../../@shared/routableNotEditableLink';
import ExperienceEditorComponentPlaceholder from '../../../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../../../@shared/emptyApiDataMessage';
import Picture from '../../../@shared/picture';
import Icon from '../../../@shared/icon';
import TextComponentDecider from '../../../@shared/TextComponentDecider';
import { getTextType } from '../../../../utils/getTextType.utils';
import '../../styles.scss';

const ContentHubCompatibleLoaders: FC<TCHCompatibleLoadersProps> = (props: TCHCompatibleLoadersProps) => {
  const model = useMemo(() => new ProductCompatibleLoaderListingModel(props), [props]);
  const apiResponse = isEmptyResponse(props);
  const apiDataLength = props?.fields?.apiData?.items?.length > 0;
  const isChildrenPopulated = props?.fields?.apiData?.items?.some((e) => e?.items?.length > 0);
  // check with BA about this condition
  const headingPopulated = model.eyebrow || model.title || model.description;
  const checkEmptiness = apiResponse && apiDataLength && isChildrenPopulated;
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [showMoreLoaders, setShowMoreLoader] = useState(false);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);

  useEffect(() => setShowMoreLoader(false), [selectedTab]);

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!checkEmptiness && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!checkEmptiness && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  const selectedTabItems = model?.tabs?.find((_, index: number) => index === selectedTab)?.items;

  const selectTabEvent = (tabIndex) => {
    setSelectedTab(tabIndex);
  };

  const showMoreLoaderEvent = (showMoreLoaders) => {
    setShowMoreLoader(showMoreLoaders);
    if (!showMoreLoaders) {
      const element = documentService().getElementById(props.id);
      const headerOffset = 85;
      const elementPosition = element?.getBoundingClientRect().top;
      const offsetPosition = elementPosition ? elementPosition + windowService().pageYOffset - headerOffset : 0;

      windowService().scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
    googleSeeMoreCardEvent(model?.tabs?.[selectedTab]?.title, selectedTab + 1);
  };

  return (
    <section>
      <article className={'product-compatible-loaders-listing'} id={props.id}>
        <AnalyticsVisibleEvent
          rendering={props?.rendering as ComponentRendering}
          title={props?.fields?.apiData?.title}
          template={model?.templateName}
        >
          {headingPopulated && (
            <header className={'product-compatible-loaders-listing__header'}>
                <TextComponentDecider
                  type={getTextType('Text', !model?.eyebrow?.value)}
                  tag={'p'}
                  field={{ value: model?.eyebrow?.value }}
                  className="product-compatible-loaders-listing__eyebrow"
                  dictionaryKey={props.eyebrowKeyCompatibleLoaders}
                />

                <TextComponentDecider
                  type={getTextType('Text', !model.title?.value)}
                  tag={'h3'}
                  field={{ value: model.title?.value }}
                  className="product-compatible-loaders-listing__title"
                  dictionaryKey={props.titleKeyCompatibleLoaders}
                />

                <TextComponentDecider
                  type={getTextType('RichText', !model.description?.value)}
                  tag={'div'}
                  field={{ value: model.description?.value }}
                  className="product-compatible-loaders-listing__description rich-text"
                  dictionaryKey={props.descriptionKeyCompatibleLoaders}
                />
            </header>
          )}

          {model.tabs?.length > 1 && <CompatibleLoadersTabs tabs={model.tabs} selectedTab={selectedTab} onSelect={selectTabEvent} />}

          <div className={'product-compatible-loaders-listing__content-container'}>
            <section className={'product-compatible-loaders-listing__content'}>
              {model?.tabs
                .find((_, index: number) => index === selectedTab)
                ?.items?.filter((_, index) => (!showMoreLoaders && index < 4) || showMoreLoaders)
                .map((loader, itemIndex) => (
                  <RoutableNotEditableLink
                    key={loader?.id}
                    href={appendToUrlPath(model.baseUrl || '', loader?.url || '')}
                    onClick={(): void => {
                      loader?.url && googleClickCardEvent(model?.tabs?.[selectedTab]?.items?.[itemIndex]?.title, itemIndex + 1);
                    }}
                    className={'product-compatible-loaders-listing__link'}
                  >
                    <Picture
                      productImageProps={{
                        desktopRendition: loader?.assets[0]?.rendition_desktop_size500,
                        mobileRendition: loader?.assets[0]?.rendition_mobile_size500,
                        alt: loader?.assets[0]?.alt,
                        imageClassName: `product-compatible-loaders-listing__card-image ${
                          loader?.url ? '' : 'product-compatible-loaders-listing__card-image--link-none'
                        }`,
                      }}
                    />

                    <h4
                      className={`product-compatible-loaders-listing__card-title ${
                        loader?.url ? '' : 'product-compatible-loaders-listing__card-title--link-none'
                      }`}
                    >
                      {loader?.title}
                    </h4>

                    <RichText
                      onClick={(e) => {
                        e.stopPropagation();
                        googleClickRichTextEvent(e, model?.tabs?.[selectedTab]?.items?.[itemIndex]?.title, selectedTab + 1);
                      }}
                      className={'product-compatible-loaders-listing__card-text rich-text'}
                      field={{ value: appendRootToRichTextInternalLinks(loader?.description) }}
                    />
                  </RoutableNotEditableLink>
                ))}
            </section>
            {selectedTabItems && selectedTabItems?.length > 4 && (
              <section className={'product-compatible-loaders-listing__show-more-section'}>
                <button
                  className={'product-compatible-loaders-listing__show-more'}
                  onClick={(): void => showMoreLoaderEvent(!showMoreLoaders)}
                >
                  <span>{!showMoreLoaders ? t(props.seeMoreKey) : t(props.seeLessKey)}</span>
                  <Icon iconName={!showMoreLoaders ? 'arrow-chevron-down' : 'arrow-chevron-up'} size={iconSize.dsk} />
                </button>
              </section>
            )}
          </div>
        </AnalyticsVisibleEvent>
      </article>
    </section>
  );
};

export default withSitecoreContext()(ContentHubCompatibleLoaders);
